import { Zone } from "@apis/index";
import Button from "@components/button";
import Column from "@components/column";
import Fields from "@components/fields";
import DateTimePicker from "@components/fields/datetime";
import Row from "@components/row";
import Typography from "@components/typography";
import FieldWrapper from "@components/wrapper/field-wrapper";
import { Form, Formik } from "formik";
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";

interface SurgeFormProps {
  onRefresh: () => void;
  editingData?: any;
  onClearEdit: () => void;
  hasActiveSurge: boolean;
}

const SurgeForm: React.FC<SurgeFormProps> = ({
  onRefresh,
  editingData,
  onClearEdit,
  hasActiveSurge,
}) => {
  const { zoneId } = useParams();

  const initialValues = useMemo(() => {
    if (editingData) {
      return {
        title: editingData.title || "",
        vehicletype: editingData.vehicleType || "",
        surgeType: editingData.surgeType || "",
        amount: editingData.surgecharge?.toString() || "",
        startdate: editingData.startDate || "",
        enddate: editingData.endDate || "",
        starttime: editingData.startTime || "",
        endtime: editingData.endTime || "",
        status: editingData.status || false,
      };
    }
    return {
      title: "",
      vehicletype: "",
      surgeType: "",
      amount: "",
      startdate: "",
      enddate: "",
      starttime: "",
      endtime: "",
      status: false,
    };
  }, [editingData]);

  const handleSubmit = async (
    values: typeof initialValues,
    { resetForm }: any,
  ) => {
    const formatDate = (d: any) => {
      if (!d) return null;
      const date = d instanceof Date ? d : new Date(d);
      return isNaN(date.getTime()) ? d : date.toISOString();
    };

    const payload = {
      title: values.title,
      vehicleType: values.vehicletype,
      surgeType: values.surgeType,
      surgecharge: Number(values.amount),
      startDate: formatDate(values.startdate),
      endDate: formatDate(values.enddate),
      startTime: values.starttime,
      endTime: values.endtime,
      zoneId: Number(zoneId),
      status: values.status,
    };

    try {
      if (editingData) {
        await Zone.patchSurgePrice({
          id: editingData.id,
          data: payload,
        });
        onClearEdit();
      } else {
        await Zone.updateSurgePrice({
          id: zoneId,
          data: payload,
        });
      }
      resetForm();
      onRefresh();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ resetForm, values }) => (
        <FieldWrapper>
          <Typography styleText="font-weight:600;font-size:18px">
            {editingData ? "Edit Surge Price" : "Add Surge Price Information"}
          </Typography>
          <br />
          <Form>
            <Column>
              <Row>
                <Fields.Input
                  name="title"
                  placeholder="Enter Title"
                  label="Title"
                />
                <Fields.DropDown
                  name="vehicletype"
                  placeholder="--Select Vehicle type---"
                  label="Vehicle type"
                  options={[
                    { id: "both", name: "Both" },
                    { id: "two-wheeler", name: "2 Wheeler" },
                    { id: "four-wheeler", name: "4 Wheeler" },
                  ]}
                  valueAsKey="id"
                  titleAsKey="name"
                  styleInput="width:94.5%"
                />
                <Fields.DropDown
                  name="surgeType"
                  placeholder="--Select Surge type---"
                  label="Surge type"
                  options={[
                    { id: "flat", name: "Flat" },
                    { id: "percentage", name: "Percentage" },
                  ]}
                  valueAsKey="id"
                  titleAsKey="name"
                  styleInput="width:94.5%"
                />
                <Fields.Input
                  name="amount"
                  placeholder="Enter Amount"
                  label="Amount"
                />
              </Row>

              <Row>
                <DateTimePicker
                  placeholder="Select Start Date"
                  label="Start Date"
                  name="startdate"
                />
                <DateTimePicker
                  placeholder="Select End Date"
                  label="End Date"
                  name="enddate"
                />
                <DateTimePicker
                  placeholder="Select Start Time"
                  label="Start Time"
                  name="starttime"
                  showTimeSelect={true}
                />
                <DateTimePicker
                  placeholder="Select End Time"
                  label="End Time"
                  name="endtime"
                  showTimeSelect={true}
                />
              </Row>
              <Row styleRow="align-items: center; justify-content: space-between;">
                <Fields.Switch
                  name="status"
                  label="Active Status"
                  disabled={
                    hasActiveSurge && (!editingData || !editingData.status)
                  }
                  styleWrapper="width: fit-content; margin-top: 10px;"
                />
                <Row styleRow="justify-content: flex-end; gap: 10px; width: fit-content;">
                  {editingData && (
                    <Button
                      title="Cancel"
                      type="button"
                      styleButton="background-color: #f3f4f6; color: #374151; border: 1px solid #d1d5db;"
                      onClick={() => {
                        onClearEdit();
                        resetForm();
                      }}
                    />
                  )}
                  <Button
                    title={
                      editingData ? "Update Surge Price" : "Save Surge Price"
                    }
                    type="submit"
                  />
                </Row>
              </Row>
            </Column>
          </Form>
        </FieldWrapper>
      )}
    </Formik>
  );
};

export default SurgeForm;
